import moment from "moment";
import InlineSVG from "react-inlinesvg";
import { SudoButton } from "src/sudokit/components/SudoButton";
import { getPlanTransitionType, PLAN_TRANSITION_TYPE } from "../../../../../src/shared/constants";
import { getChangeVerb, formatCredits, formatMonthlyPrice, getRolloverInfo } from "./planChangeHelpers";
import { Container, Overlay, ComparisonSection, RolloverWarning, RolloverBenefit } from "./styles";

const PricingModal = ({ subscription, currentPlan, targetPlan, setModalOpen, handleSelectPlan, submitting }) => {
  const periodEnd = subscription.nextBillingDate();
  const endMillis = new Date(periodEnd).getTime();
  const now = new Date().getTime();
  const daysLeftInCurrentPeriod = Math.floor((endMillis - now) / 1000 / 86400);
  const displayNextBilling = moment(new Date(periodEnd)).format("MMMM Do, YYYY");
  const isV4Transition = currentPlan.version[0] !== "4" && targetPlan.version[0] === "4";

  // TODO: handle the case where there is no current plan
  const transitionType = getPlanTransitionType(currentPlan, targetPlan);

  const intervalString = targetPlan.interval + "ly";

  const { verb, gerund } = getChangeVerb(currentPlan, targetPlan);
  const { losingRollover, gainingRollover } = getRolloverInfo(currentPlan, targetPlan);

  const consumable = targetPlan.usesCredits ? "credits" : "AI words";

  return (
    <Overlay
      onClick={() => {
        if (submitting) return;
        setModalOpen(false);
      }}
    >
      <Container>
        <SudoButton
          className="close absolute top-4 right-4 p-2"
          variant="tertiary-grey"
          onClick={(e) => {
            e.preventDefault();
            if (submitting) return;
            setModalOpen(false);
          }}
        >
          <InlineSVG
            src="/assets/close.svg"
            height="24"
            width="24"
            className="fill-[var(--grey-1)]"
          />
        </SudoButton>

        <div className="content">
          <h1>
            {verb} to {targetPlan.title}
          </h1>

          <ComparisonSection>
            {currentPlan.allotment !== targetPlan.allotment && (
              <p><strong>Credits:</strong> {formatCredits(currentPlan.allotment)}/month &rarr; {formatCredits(targetPlan.allotment)}/month</p>
            )}
            <p><strong>Price:</strong> {formatMonthlyPrice(currentPlan)} &rarr; {formatMonthlyPrice(targetPlan)}</p>
            {currentPlan.interval !== targetPlan.interval && (
              <p>You're switching from {currentPlan.interval}ly to {targetPlan.interval}ly billing.</p>
            )}
          </ComparisonSection>

          {losingRollover && (
            <RolloverWarning>
              <strong>Rollover credits:</strong> Your current plan allows unused credits to roll over for 12 months. The {targetPlan.title} plan does not include credit rollover. Any accumulated rollover credits will be lost.
            </RolloverWarning>
          )}
          {gainingRollover && (
            <RolloverBenefit>
              <strong>Credit rollover:</strong> With the {targetPlan.title} plan, unused credits will roll over for up to 12 months.
            </RolloverBenefit>
          )}

          <ul>
            {transitionType === PLAN_TRANSITION_TYPE.PRORATED_REFUND_AND_TRANSITION_IMMEDIATELY && (
              <>
                <li>We'll refund you for the remaining time of {daysLeftInCurrentPeriod} days in this billing cycle.</li>

                <li>
                  You will be charged ${targetPlan.price / 100} immediately and your new {targetPlan.title} {intervalString} plan will start today with {formatCredits(targetPlan.allotment)}{" "}
                  {consumable} per month.
                </li>

                {isV4Transition && <li>Purchased non-expiring words will be converted, and you'll receive an email with details.</li>}
              </>
            )}

            {transitionType === PLAN_TRANSITION_TYPE.CANCEL_AT_PERIOD_END_AND_SCHEDULE_TRANSITION && (
              <>
                <li>
                  You'll remain on the {currentPlan.title} plan until {displayNextBilling}.
                </li>

                <li>
                  Starting on {displayNextBilling} you'll start the {targetPlan.title} {intervalString} plan with {formatCredits(targetPlan.allotment)} {consumable} / month.
                </li>
              </>
            )}

            {transitionType === PLAN_TRANSITION_TYPE.TRANSITION_NATURALLY && (
              <>
                <li>
                  You'll switch to the {targetPlan.title} {intervalString} plan on your next billing cycle.
                </li>
                <li>
                  Starting on {displayNextBilling}, you'll be charged ${targetPlan.price / 100} {intervalString} and receive {formatCredits(targetPlan.allotment)} {consumable} / month.
                </li>
              </>
            )}
          </ul>

          <SudoButton
            variant="primary"
            onClick={() => {
              handleSelectPlan(subscription, targetPlan);
            }}
          >
            {submitting ? (
              <>{gerund}...</>
            ) : (
              <>
                {verb} to {targetPlan.title}
              </>
            )}
          </SudoButton>
        </div>
      </Container>
    </Overlay>
  );
};

export default PricingModal;
